import {
  FormInput,
  SelectInput,
  SubmitButton,
  Form,
  Grid,
  NotificationsContext,
  VerificationCodeInput,
  FormElementRef,
  FormGetter,
} from '@kirz/mui-admin';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Email, Phone } from 'mdi-material-ui';
import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { PngCompanyLogo } from 'components/PngCompanyLogo';
import { api } from 'services/api';
import { LoginError, LoginPayload, UserIdentity } from 'types/auth';

const adminDomains = ['localhost:3000', 'crm.klantenmp.be'];

export function Login() {
  const formRef = useRef<FormElementRef>(null);
  const host = window.location.host;
  const redirectTo = new URLSearchParams(window.location.search).get(
    'redirectTo',
  );
  const isAdminDomain = adminDomains.includes(host);

  const [loginStep, setLoginStep] = useState<
    'email' | 'phone' | 'identity' | 'code' | 'password'
  >('email');
  const [loginMethod, setLoginMethod] = useState<'phone' | 'email' | 'unknown'>(
    'unknown',
  );
  const [identities, setIdentities] = useState<UserIdentity[]>([]);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [phoneEnding, setPhoneEnding] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null);
  const { showAlert } = useContext(NotificationsContext);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const theme = useTheme();
  const temp = useRef<string>('');

  const handleSubmit = async (payload: LoginPayload) => {
    const method = temp.current || loginMethod;
    if (temp.current) {
      // @ts-ignore
      setLoginMethod(temp.current);
      // @ts-ignore
      setLoginStep(temp.current);
      temp.current = '';
    }

    const { success, errorCode, errorData } = await api.login({
      ...payload,
      // @ts-ignore
      method: method === 'unknown' ? 'email' : method,
      redirectTo,
    });

    if (success) {
      setIsAlertVisible(true);
      // await initialize!();

      if (method === 'phone') {
        if (redirectTo) {
          window.location.href = redirectTo;
        } else {
          window.location.reload();
        }
      }

      return;
    }

    setErrorMessage(null);
    setInfoMessage(null);

    switch (errorCode) {
      // case LoginError.NO_EMAIL: {
      //   setErrorMessage(
      //     <>
      //       No email linked to your account. Please contact support team at{' '}
      //       <a href="mailto:info@maesenpartners.be">info@maesenpartners.be</a>
      //     </>,
      //   );
      //   break;
      // }
      case LoginError.IDENTITY_SELECTION_REQUIRED: {
        setLoginStep('identity');
        setIdentities(errorData.identities as UserIdentity[]);
        setInfoMessage('Uw account is gekoppeld aan meerdere bedrijven');
        break;
      }
      case LoginError.PHONE_VALIDATION_REQUIRED: {
        setLoginStep('code');
        setPhoneEnding(errorData.phoneLastTwoDigits);
        setIsAlertVisible(true);
        break;
      }

      case LoginError.PASSWORD_VALIDATION_REQUIRED: {
        setLoginStep('password');
        setInfoMessage('Voer uw wachtwoord in');
        break;
      }
      case LoginError.IDENTITY_NOT_FOUND: {
        setErrorMessage('User not found');
        setLoginStep('email');
        break;
      }
      case LoginError.WRONG_PASSWORD: {
        setErrorMessage('Wrong password');
        break;
      }
      case LoginError.WRONG_VALIDATION_CODE: {
        setErrorMessage('Wrong verification code');
        break;
      }
      case LoginError.PHONE_VALIDATION_FAILED: {
        setErrorMessage('Verkeerde verificatiecode');
        break;
      }
      default: {
        throw new Error(`Unknown error: ${errorCode}`);
      }
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('status') === 'fail') {
      showAlert('Magic link invalid or expired. Please try again', 'error', {
        autoHideDuration: 5000,
        variant: 'error',
      });

      window.history.replaceState(null, '', `${window.location.pathname}}`);
    }
  }, []);

  return (
    <Container
      maxWidth="sm"
      sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}
    >
      <Paper elevation={16} sx={{ flex: 1, p: 4, width: '100%' }}>
        <Stack
          alignItems="center"
          spacing={0.75}
          sx={{ position: 'relative', pt: 2 }}
        >
          <PngCompanyLogo
            htmlColor={theme.palette.brand.main}
            sx={{
              width: 250,
              position: 'absolute',
              // left: -10,
              top: -10,
            }}
          />
          <Box sx={{ height: '27px' }} />
          <Typography variant="h4">Login</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {isAdminDomain ? 'klantenbeheerplatform' : 'platform'}
          </Typography>
          <Form
            ref={formRef}
            sx={{ pt: 2, alignSelf: 'stretch' }}
            onSubmit={handleSubmit}
            spacing={2}
            shouldUnregister={false}
            dirtySubmit={false}
          >
            {infoMessage && (
              <Grid xs={12}>
                <Alert severity="warning">{infoMessage}</Alert>
              </Grid>
            )}
            {errorMessage && (
              <Grid xs={12}>
                <Alert severity="error">{errorMessage}</Alert>
              </Grid>
            )}

            {isAlertVisible && (
              <Grid xs={12}>
                <Alert severity="info" sx={{ fontSize: 14, lineHeight: 1.4 }}>
                  {loginMethod === 'email'
                    ? `We hebben u een e-mail gestuurd met een link. Controleer uw
                  inbox en klik op de link om in te loggen. Controleer ook uw
                  spam en zet onze mail bij de toegestane mails om dat in de
                  toekomst te voorkomen.`
                    : `We hebben u een code via SMS verstuurd naar het telefoonnummer eindigend op ${
                        phoneEnding ?? ''
                      }. Voer deze code hieronder in om toegang te krijgen tot het platform.`}
                </Alert>
              </Grid>
            )}
            <>
              <Grid xs={12}>
                <Typography variant="body2">
                  Vul hieronder uw e-mail adres in en kies daarna of u wil
                  inloggen via e-mail of sms
                </Typography>
              </Grid>
              <FormInput
                label="E-mail"
                clearable
                required
                name="email"
                type="email"
                disabled={loginMethod !== 'unknown'}
                autoFocus
                placeholder="your-email@gmail.com"
              />

              {loginStep === 'identity' && (
                <SelectInput
                  label="Bedrijf"
                  required
                  name="identityId"
                  items={identities.map((x) => ({
                    value: x.id,
                    text: `${x.userName}`,
                  }))}
                />
              )}

              {loginStep === 'code' && (
                <VerificationCodeInput
                  name="verificationCode"
                  required
                  length={5}
                  TextFieldsProps={{
                    className: 'no-spinners',
                    type: 'number',
                    inputProps: {
                      itemType: 'number',
                      pattern: '[0-9]*',
                      inputMode: 'numeric',
                      className: 'no-spinners',
                    },
                  }}
                  {...({} as any)}
                />
              )}

              <FormGetter
                names={['email']}
                render={({ email }) =>
                  loginMethod === 'unknown' ? (
                    <>
                      <Grid xs={12} md={6}>
                        <Button
                          variant="contained"
                          size="large"
                          fullWidth
                          startIcon={<Email />}
                          disabled={!email}
                          onClick={() => {
                            temp.current = 'email';
                            // setLoginMethod('email');
                            // setLoginStep('email');

                            formRef.current?.submit();
                          }}
                        >
                          Inloggen via mail
                        </Button>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <Button
                          variant="contained"
                          size="large"
                          fullWidth
                          startIcon={<Phone />}
                          disabled={!email}
                          onClick={() => {
                            temp.current = 'phone';
                            //  setLoginMethod('phone');
                            //  setLoginStep('phone');

                            formRef.current?.submit();
                          }}
                        >
                          Inloggen via sms
                        </Button>
                      </Grid>
                    </>
                  ) : (
                    (!isAlertVisible || loginMethod === 'phone') && (
                      <SubmitButton
                        variant="contained"
                        size="large"
                        grid
                        fullWidth
                        xs={12}
                      >
                        Inloggen
                      </SubmitButton>
                    )
                  )
                }
              />
            </>
          </Form>
        </Stack>
      </Paper>
    </Container>
  );
}
